import { get } from 'lodash';
import { addToCartItems } from '~/api/cart';
import { canUseDOM } from './executionEnvironment';
import Storage from './StorageManager';
import redirectToPreviewAPI from './previewApp';
import verifyPxCaptcha from '../modules/pxCaptchaModal/verifyPxCaptcha';
import {
  sessionAPI,
  akamaiCoordURL,
  UNAUTHORIZED_STATUS,
  HTTP_STATUS_CODE_201,
  HTTP_STATUS_CODE_401,
  AK_LOCATED_ZIP_CODE,
  AK_LOCATED_LAT_LNG,
  blockWithCredentials,
  UNKNOWN,
  UNKNOWN_MSG,
  ERROR_SESSION_KEY,
  COOKIE_USERTYPE,
  COOKIE_USERACTIVITY,
  TAX_SERVICE_URL,
  GUEST_IDENTITY,
  HQ_ZIP_CODE,
  HQ_LAT_LNG,
  ERR_PARTIAL_AUTHENTICATION,
  LIST_API_URL,
  GEO_LOCATED_REGION_CODE,
  ADD_CHANNEL_HEADER_FOR_REQUESTS,
  ADD_CHANNEL_HEADER_FOR_PREVIEW_REQUESTS,
} from './constants';
import { deletePersistentSession } from './persistentSession';
import { getRegistrationStatus } from './UserSession';

let FALLBACK_401 = true;

/**
 * This function gets the original array and the parsed error object.
 * If status is unknown, then it shows error object else unknow error message
 * Otherwise the detail of the error object
 * @param {Object} orgError Original error object
 * @param {Object} error The error to be displayed
 */
const showError = (orgError, error, apiUrl) => {
  if (!Storage.getSessionStorage(ERROR_SESSION_KEY)) {
    return;
  }
  const { response } = error;
  if (response.status === UNKNOWN) {
    if (orgError.response && orgError.response.data) {
      const { config, data } = orgError.response;
      console.error(`ERROR :: __status: ${data && data.status} :: __url: ${config && config.url}`);
    }
    // eslint-disable-next-line
    console.error('ERROR :: ', orgError || UNKNOWN_MSG);
    if (apiUrl) {
      // eslint-disable-next-line
      console.error('Above error occurred for API: ', apiUrl);
    }
  } else {
    // eslint-disable-next-line
    console.error(`ERROR :: ${response.status} :: ${response.config.url || response.url}`);
  }
};

// Below code will re-direct the user to off-site login page
const isAuthenticatedPage = url => url.match(/\/shop\/cart|\/checkout|\/myaccount|\/OrderConfirmation/);
const isCustomerCarePage = url => url.match(/\/cc/);
const isApplyPaymentPage = url => url.match(/\/applypayment/);

/**
 * Function to override the request config object
 * @param {object} config The congig object sent by axios.
 */
const axiosRequestOverride = config => {
  // check if isAuthor is true, set the mode to stage in header
  if (window.ASOData.isAuthor) {
    // eslint-disable-next-line
    config.headers.mode = 'stage';
    Storage.setCookie('mode', 'stage');
  }

  // for preview mode, redirect to staging APIs
  const { uiConfig: { cmsGlobal = {}, mode } = {} } = window.ASOData;
  let newPreviewAPI = config.url;
  if (mode === 'stage') {
    newPreviewAPI = redirectToPreviewAPI(config.url, cmsGlobal.pricePreview);
  }
  if (cmsGlobal.enableChannelRequestHeader) {
    const urlWithOutQueryParams = newPreviewAPI?.split('?')[0];
    const isUrlPresentInPromotionReqUrls =
      mode === 'stage'
        ? ADD_CHANNEL_HEADER_FOR_PREVIEW_REQUESTS.some(item => item?.test(urlWithOutQueryParams))
        : ADD_CHANNEL_HEADER_FOR_REQUESTS.some(item => item?.test(urlWithOutQueryParams));
    if (isUrlPresentInPromotionReqUrls) {
      // eslint-disable-next-line no-param-reassign
      config.headers.channel = 'web';
    }
  }
  let configOverride = {};
  // Do merge/override the config before request is sent
  configOverride = { ...config, withCredentials: blockWithCredentials(newPreviewAPI), url: newPreviewAPI };
  return configOverride;
};

const axiosRequestThrottler = config => config;

/**
 * Function to override the error received from a failed request
 * @param {object} error The error object
 */
const axiosRequestErrorOverride = error => Promise.reject(error);

/**
 * Method to validate guest identity API response and reload page for 201/400/401 status
 */
const getGuestIdentityFallBackConfig = () => ({
  validateStatus: validateStatus => {
    if (validateStatus === HTTP_STATUS_CODE_201) {
      window.location.reload(true);
    } else if (validateStatus === HTTP_STATUS_CODE_401) {
      // get all cookies from document
      const cookies = Storage.getAllCookies();
      // delete cookies which starts with WC_
      cookies.forEach(item => {
        if (item.indexOf('WC_') > -1) {
          const cookie = item.split('=');
          if (cookie.length) {
            Storage.deleteCookie(cookie[0]);
          }
        }
      });
      Storage.deleteCookie('USERACTIVITY');
      Storage.deleteCookie('USERTYPE');
      Storage.removeLocalStorage('userProfileData');
      // window.location.reload(true);
    }
    return true;
  },
});

/**
 * Function to create script tags dynamically for the component JS files.
 */
const createComponentScripts = () => {
  if (window.ASOData && window.ASOData.componentScripts && Array.isArray(window.ASOData.componentScripts)) {
    window.ASOData.componentScripts.forEach(src => {
      const componentScriptTag = document.createElement('script');
      componentScriptTag.src = src;
      document.body.appendChild(componentScriptTag);
    });
  }
};

/** Class representing the axios interceptor */
export default class AxiosInterceptor {
  constructor(axios) {
    this.axios = axios;
    this.axiosResponseErrorOverride = this.axiosResponseErrorOverride.bind(this);
    this.fetchZipCodeFromUsingAkamai = this.fetchZipCodeFromUsingAkamai.bind(this);
  }

  /**
   * Function to log the error received from a response
   * @param {object} error The error object
   * @return {boolean} false return to exit the function
   */
  axiosResponseErrorOverride(error) {
    // If `apiError` not found in error object or error object is not available, set the status to unknown
    // to handle it differently
    const { config: { url = '' } = {} } = error;
    const apiError = (error && error.apiError) || { response: { status: UNKNOWN } };
    showError(error, apiError);
    const status = get(error, 'response.status', 0);
    const isUnauthorized = UNAUTHORIZED_STATUS.indexOf(status) > -1;
    const unauthenticatedErrorKey = get(error, 'response.data.errorMap.UNAUTHENTICATED', '');
    if (isUnauthorized && canUseDOM) {
      const decodedCookies = decodeURIComponent(document.cookie);
      const wcUserActivity = decodedCookies.match(/WC_USERACTIVITY_(-)?\d+(?==)/); // Regex to extract cookie name
      const wcAuthentication = decodedCookies.match(/WC_AUTHENTICATION_(-)?\d+(?==)/); // Regex to extract cookie name
      const previousDay = new Date();
      previousDay.setTime(previousDay.getTime() + 0); // create date as previous day

      if (get(window, 'ASOData.uiConfig.cmsGlobal.isPXEnabled', false) && error.response.status === 403 && get(error, 'response.data.appId', false)) {
        return verifyPxCaptcha(error.response.data).then(isPxValid => (isPxValid ? this.axios(error.config) : error));
      }

      const isUserActivityCookiePresent = wcUserActivity ? wcUserActivity[0] : null;
      const isUserAuthenticationCookiePresent = wcAuthentication ? wcAuthentication[0] : null;

      if (isUserActivityCookiePresent) {
        Storage.setCookie(`${isUserActivityCookiePresent}`, '', previousDay.toUTCString()); // set expiry date to previous date to delete cookie
      }
      if (isUserAuthenticationCookiePresent) {
        Storage.setCookie(`${isUserAuthenticationCookiePresent}`, '', previousDay.toUTCString()); // set expiry date to previous date to delete cookie
      }
      if (unauthenticatedErrorKey !== ERR_PARTIAL_AUTHENTICATION) {
        Storage.setCookie(`${COOKIE_USERTYPE}`, 'G');
        Storage.setCookie(`${COOKIE_USERACTIVITY}`, '-1002');
        deletePersistentSession();
      }
      let pageURL = window.location.href;
      if (pageURL.indexOf('?') > -1) {
        pageURL = pageURL.indexOf('debug=aso') > -1 ? pageURL : pageURL.split('?').join('?debug=aso&');
      } else {
        pageURL = pageURL.indexOf('#') > -1 ? pageURL.split('#').join('?debug=aso#') : `${pageURL}?debug=aso`;
      }
      pageURL = encodeURI(pageURL);
      // window.location = `/shop/Logoff?debug=aso&rememberMe=true&URL=${pageURL}`;
      // window.location = `/shop/LogonForm?URL=${pageURL}`;
      // window.location = '/shop/LogonForm';
      // eslint-disable-next-line consistent-return
      if (isApplyPaymentPage(window.location.href)) return; // For Apply payment page, no redirection is required to any login screen
      if (
        isAuthenticatedPage(window.location.href) ||
        url === addToCartItems ||
        TAX_SERVICE_URL.test(url) ||
        (unauthenticatedErrorKey === ERR_PARTIAL_AUTHENTICATION && url === LIST_API_URL)
      ) {
        window.location.href = '/shop/LogonForm?timeout=true';
      } else if (FALLBACK_401) {
        if (isCustomerCarePage(window.location.href)) {
          window.location.href = `/cc/login${window.location.search}`;
        }
        FALLBACK_401 = false;
        this.axios
          .post(GUEST_IDENTITY, {}, getGuestIdentityFallBackConfig())
          .then(() => {
            if (get(window, 'ASOData.uiConfig.cmsGlobal.alwaysRedirectToLoginOnAuthorizationError', false)) {
              window.location.href = '/shop/LogonForm?timeout=true';
            }
          })
          .catch(err => {
            console.error(err);
          });
      }
    } /* else if (error && error.response && (error.response.status === 400 || error.response.status === 403) && canUseDOM) { // fallback to handle invalid activity and unauthorized query
      window.location.href = '/';
    } */
    if (error.response && error.response.data) {
      return Promise.reject(error.response);
    }
    return false;
  }

  /**
   * Function to declare response interceptor for tracking API call errors
   */
  axiosResInterceptor() {
    this.axios.interceptors.response.use(response => response, this.axiosResponseErrorOverride);
  }

  /**
   * Function to initialize the axios interceptor
   */
  axiosReqInterceptor() {
    // Add a request interceptor
    this.axios.interceptors.request.use(axiosRequestOverride, axiosRequestErrorOverride);
    this.axios.interceptors.request.use(axiosRequestThrottler, axiosRequestErrorOverride);
  }

  /**
   * Function to set session cookie timer for 30mins.
   */
  // eslint-disable-next-line class-methods-use-this
  setSessionExpiryto30Minutes() {
    const now = new Date();
    const minutes = 30;
    now.setTime(now.getTime() + (minutes * 60 * 1000)); // prettier-ignore
    return now.toUTCString();
  }

  /**
   * Function to get and set session cookies prior to all component calls.
   */
  getSessionInfo() {
    // make session api call only if session_expiry is not available in cookie
    if (!Storage.getCookie('session_expiry')) {
      Storage.setLocalStorage('session_refreshed', 'true');
      // The below flag used in FindAStoreModal component, when user didnt close the browser(It may happen in phone) at the time value in local storage remains same for store info.
      // This fix is mainly for changing bopisEligible flag, if value got updated in db then it needs to update in browser localstorage. The flag will be deleted in FSM component.
      Storage.setLocalStorage('session_expired_fsm', 'true');
      this.axios
        .get(sessionAPI)
        .then(response => {
          // TODO - temp code to mitigate 401 errors
          if (response === 401 && canUseDOM) {
            const matches = decodeURIComponent(document.cookie).match(/WC_USERACTIVITY_(-)?\d+(?==)/);
            const result = matches ? matches[0] : null;
            if (result) {
              document.cookie = `${result}=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
              this.getSessionInfo();
            }
          }
          createComponentScripts();
        })
        .catch(error => {
          createComponentScripts();
          // If `response` not found in error object or error object is not available, set the status to unknown
          // to handle it differently
          const responseError = (error && error.response) || { response: { status: UNKNOWN } };
          showError(error, responseError, sessionAPI);
        });
    } else {
      // load the scripts.
      createComponentScripts();
    }
    const metaTrackingFlag = get(window, 'ASOData.uiConfig.cmsGlobal.ENABLE_META_TRACKING', false);
    const isRegisteredUser = getRegistrationStatus();
    if (canUseDOM && metaTrackingFlag && !isRegisteredUser) {
      Storage.removeLocalStorage('userProfileData');
    }
  }

  fetchZipCodeFromUsingAkamai = () => {
    if (canUseDOM && !Storage.getLocalStorage(AK_LOCATED_ZIP_CODE)) {
      this.axios
        .get(akamaiCoordURL)
        .then(response => {
          const { data = {}, status } = response;
          if (status === 200) {
            const { latitude = '', longitude = '', regionCode = '' } = data;
            const currentRegionCode = Storage.getSessionStorage(GEO_LOCATED_REGION_CODE);
            if (regionCode && !currentRegionCode) {
              Storage.setSessionStorage(GEO_LOCATED_REGION_CODE, regionCode);
            }
            const { google: { maps: { GeocoderStatus: { OK = '' } = {}, Geocoder = () => null, LatLng = () => null } = {} } = {} } = window;
            const googleLatLong = new LatLng(latitude, longitude);
            const geocoder = new Geocoder();
            geocoder.geocode({ latLng: googleLatLong }, (geoCodeResults, geoCodeStatus) => {
              if (geoCodeStatus === OK) {
                const { address_components: addressComponents = [] } = geoCodeResults[0];
                const extractedZip = addressComponents.filter(item => item && item.types.indexOf('postal_code') > -1);
                if (extractedZip.length) {
                  const { long_name: zipCode } = extractedZip[0];
                  if (zipCode) {
                    Storage.setLocalStorage(AK_LOCATED_ZIP_CODE, zipCode);
                    Storage.setLocalStorage(AK_LOCATED_LAT_LNG, data);
                  }
                }
              } else {
                Storage.setLocalStorage(AK_LOCATED_ZIP_CODE, HQ_ZIP_CODE);
                Storage.setLocalStorage(AK_LOCATED_LAT_LNG, HQ_LAT_LNG);
              }
            });
          } else {
            Storage.setLocalStorage(AK_LOCATED_ZIP_CODE, HQ_ZIP_CODE);
            Storage.setLocalStorage(AK_LOCATED_LAT_LNG, HQ_LAT_LNG);
          }
        })
        .catch(() => {
          Storage.setLocalStorage(AK_LOCATED_ZIP_CODE, HQ_ZIP_CODE);
          Storage.setLocalStorage(AK_LOCATED_LAT_LNG, HQ_LAT_LNG);
        });
    }
  };

  /**
   * Function to initialize the interceptor and the session info
   */
  initialize() {
    this.fetchZipCodeFromUsingAkamai();
    this.axiosReqInterceptor();
    this.axiosResInterceptor();
    this.getSessionInfo();
  }
}
